import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Bot, User, Minimize2 } from "lucide-react";
import api from "../utils/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useCurrentUser from "../hooks/useCurrentUser";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default function ChatAssistant() {
  const { clientId, userId } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [pendingTicket, setPendingTicket] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! How can I assist you today?" }
  ]);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const supportKeywords = ["invoice", "shipment problem", "customs issue", "billing", "documents", "support", "problem", "issue"];

  const handleSend = async () => {
    if (!input.trim()) return;
    if (!clientId) {
      alert("Missing client ID");
      return;
    }

    const userInput = input.trim();
    const userMessage = { role: "user", content: userInput };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    // Handle ticket confirmation
    if (pendingTicket && (userInput.toLowerCase().includes("yes") || userInput.toLowerCase().includes("please") || userInput.toLowerCase().includes("ok"))) {
      try {
        const ticketRef = await addDoc(collection(db, "tickets"), {
          ticketNumber: `TGKB-${Math.floor(100000 + Math.random() * 900000)}`,
          userId: userId || "anonymous",
          companyId: clientId,
          subject: `Chatbot support request: ${messages[messages.length - 2]?.content.substring(0, 50)}...`,
          category: "general",
          priority: "normal",
          status: "open",
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          source: "chatbot"
        });

        await addDoc(collection(db, "ticketMessages"), {
          ticketId: ticketRef.id,
          senderId: "system",
          message: "Ticket created via chatbot request.",
          timestamp: serverTimestamp()
        });

        // Add notification for Super Admin
        await addDoc(collection(db, "notifications"), {
          userId: "admin_user",
          message: `New chatbot ticket from ${clientId}`,
          type: "new_ticket",
          ticketId: ticketRef.id,
          read: false,
          createdAt: serverTimestamp()
        });

        setMessages((prev) => [...prev, { role: "assistant", content: "Great! I've opened a support ticket for you. You can track it in the 'My Support Tickets' section. Is there anything else I can help with?" }]);
        setPendingTicket(false);
        return;
      } catch (err) {
        console.error("Error creating ticket via chatbot:", err);
      }
    }

    if (pendingTicket) {
      setPendingTicket(false);
    }

    // Keyword detection
    const shouldOfferTicket = supportKeywords.some(kw => userInput.toLowerCase().includes(kw));

    try {
      const res = await api.post("/api/chat", {
        message: userInput,
        clientId,
      });

      const botReply = {
        role: "assistant",
        content: res.data.reply,
      };

      setMessages((prev) => [...prev, botReply]);

      if (shouldOfferTicket) {
        setTimeout(() => {
          setMessages((prev) => [...prev, {
            role: "assistant",
            content: "I noticed you're mentioning a potential support issue. Would you like me to open an official support ticket for you so our team can investigate this immediately?"
          }]);
          setPendingTicket(true);
        }, 800);
      }
    } catch (err) {
      console.error("❌ Chat error", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again later." },
      ]);
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      {open ? (
        <div className="w-[22rem] h-[32rem] bg-white shadow-2xl rounded-2xl flex flex-col border border-slate-200 animate-in slide-in-from-bottom-5 duration-300 font-sans">

          {/* Header */}
          <div className="bg-navy-900 text-white p-4 rounded-t-2xl flex justify-between items-center shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-white/10 p-1.5 rounded-lg">
                <Bot size={20} className="text-gold-400" />
              </div>
              <div>
                <h3 className="font-bold text-sm tracking-wide font-serif">TinyGlobal AI</h3>
                <span className="flex items-center gap-1.5 text-xs text-slate-300">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                  Online
                </span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-300 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
            >
              <Minimize2 size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50"
          >
            {messages.map((msg, i) => {
              const isUser = msg.role === "user";
              return (
                <div
                  key={i}
                  className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
                >
                  {!isUser && (
                    <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0 border border-navy-200">
                      <Bot size={14} className="text-navy-700" />
                    </div>
                  )}

                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${isUser
                      ? "bg-navy-900 text-white rounded-br-none"
                      : "bg-white text-slate-700 border border-slate-200 rounded-bl-none"
                      }`}
                  >
                    {isUser ? (
                      <div>{msg.content}</div>
                    ) : (
                      <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:text-navy-900 prose-strong:text-navy-900">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {isUser && (
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-slate-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-slate-100 rounded-b-2xl">
            <div className="relative flex items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask a question..."
                className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 focus:bg-white transition-all text-sm outline-none text-slate-700 placeholder:text-slate-400"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="absolute right-2 p-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:hover:bg-navy-900 transition-all shadow-md shadow-navy-900/20 active:scale-95"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="text-center mt-2">
              <span className="text-[10px] text-slate-400 font-medium">Powered by TinyGlobal AI</span>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="group relative flex items-center justify-center w-14 h-14 bg-navy-900 text-white rounded-full shadow-lg shadow-navy-900/30 hover:bg-navy-800 hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <MessageCircle size={28} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></span>
        </button>
      )}
    </div>
  );
}
